import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { Form } from "antd"

// components
import InnerModal from "components/InnerModal/InnerModal"
import ReadOnlyField from "components-design-system/form-fields/ReadOnlyField/ReadOnlyField"
import { FormButtons, PrimeButton, Select } from "components-design-system"

// api
import { useGetUsersList } from "api/hooks/users/useUsersApi"

// assets
import deleteImg from "assets/images/modal-delete.png"

// content
import { userToString } from "utilities/useUsers"

// styles
import "./MyTeamModal.scss"

const TeamModal = ({ team, selectedRole, show, setShow, onPostTeam, roles }) => {
  const [form] = Form.useForm()
  const [showDelete, setShowDelete] = useState(false)
  const [loading, setLoading] = useState(false)
  const rolesId = Form.useWatch("role_id", form)
  const { data: users } = useGetUsersList()

  // on add mode (null selectedRole), when selecting a role, populate the person IDs if role already contains team members
  useEffect(() => {
    if (!selectedRole) {
      const foundTeam = team.find((group) => group.role_id === rolesId)
      if (foundTeam) {
        const currentPersonIds = form.getFieldValue("person_ids") || []
        const newPersonIds = [...new Set([...currentPersonIds, ...foundTeam.members.map((person) => person.person_id)])]

        form.setFieldsValue({
          person_ids: newPersonIds,
        })
      }
    }
  }, [rolesId])

  const getUserOptions = (users) => {
    if (!users) return []
    return users.map((user) => {
      return { value: user.id, label: userToString(user) }
    })
  }

  const getRoleOptions = (roles) => {
    if (roles) {
      return roles.map((role) => {
        return { value: role.id, label: role.name }
      })
    }
    return []
  }

  const handleSubmitRole = async (submitValues) => {
    setLoading(true)

    const payload = {
      role_id: submitValues.role_id,
      person_ids: submitValues.person_ids,
    }

    await onPostTeam({ ...payload }).then((resp) => {
      setShow(false)
      setShowDelete(false)
    })
    setLoading(false)
  }

  const handleDeleteRole = async () => {
    const formValues = form.getFieldsValue()
    const roleId = formValues.role_id || selectedRole?.role_id
    const payload = { role_id: roleId, person_ids: [] }

    await onPostTeam(payload)
    setShow(false)
    setShowDelete(false)
  }

  if (showDelete) {
    return (
      <InnerModal
        show={show}
        setShow={setShow}
        containerStyle={{ borderRadius: 13 }}
        modalStyle={{ maxWidth: 261 }}
        wrapClassName="add-team-modal"
        className="add-team-modal-content"
        footerActions={
          <div className="add-team-modal-footer-actions">
            <FormButtons
              onCancel={() => setShowDelete(false)}
              okText="Delete"
              onOk={() => handleDeleteRole()}
              okButtonProps={{
                danger: true,
                style: { width: "100%" },
              }}
            />
          </div>
        }
      >
        <div className="delete-role-container">
          <img
            className="delete-role-img"
            src={deleteImg}
            alt="delete-role"
          />
          <div className="delete-role-title">Delete study role?</div>
          <div className="delete-role-subtitle">Are you sure you want to delete it? You can't undo this action.</div>
        </div>
      </InnerModal>
    )
  }

  return (
    <Form
      form={form}
      onFinish={handleSubmitRole}
      initialValues={{
        role_id: selectedRole?.role_id || null,
        person_ids: selectedRole?.members?.map((member) => member.person_id) || [],
      }}
    >
      <InnerModal
        show={show}
        setShow={setShow}
        containerStyle={{ borderRadius: 13 }}
        modalStyle={{ maxWidth: 261 }}
        wrapClassName="add-team-modal"
        className="add-team-modal-content"
        footerActions={
          <div className="add-team-modal-footer-actions">
            <FormButtons
              onCancel={() => setShow(false)}
              okText="Save"
              htmlType="submit"
              okButtonProps={{
                style: { width: "100%" },
                loading: loading,
              }}
            />
            {selectedRole && (
              <PrimeButton
                icon={<i className="fal fa-trash-alt delete-role-icon" />}
                danger
                onClick={() => setShowDelete(true)}
                className="delete-role-button"
              />
            )}
          </div>
        }
      >
        <div className="add-team-modal-fields">
          <Form.Item
            name="role_id"
            style={{ marginBottom: "0" }}
            rules={[{ required: true }]}
          >
            {selectedRole ? (
              <ReadOnlyField placeholder={selectedRole.role_name} />
            ) : (
              <Select
                placeholder="Role"
                options={getRoleOptions(roles)}
              />
            )}
          </Form.Item>
          <Form.Item
            name="person_ids"
            rules={[{ required: true }]}
          >
            <Select
              mode="multiple"
              placeholder="Team members"
              options={getUserOptions(users)}
              // maxRows={4} // TODO: commented out b/c causing console errors. Add max rows
            />
          </Form.Item>
        </div>
      </InnerModal>
    </Form>
  )
}

TeamModal.propTypes = {
  /**
   * team can be a study, activity, or request team
   */
  team: PropTypes.arrayOf(
    PropTypes.shape({
      role_id: PropTypes.number,
    })
  ),
  /**
   * If selectedRole is null, the modal is in add mode.
   * If selectedRole is not null, the modal is in edit mode.
   */
  selectedRole: PropTypes.shape({
    role_id: PropTypes.number,
    role_name: PropTypes.string,
    members: PropTypes.arrayOf(
      PropTypes.shape({
        person_id: PropTypes.number,
      })
    ),
  }),
  show: PropTypes.bool,
  setShow: PropTypes.func,
  onPostTeam: PropTypes.func,
  /**
   * "study/activity" vs "request" roles
   */
  roles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })
  ),
}

export default TeamModal
